<template>
  <DefaultLayout>
    <div class="page-header">
      <div>
        <h1>Consumo de Tokens</h1>
        <p class="subtitle">Monitorea el uso de tokens y peticiones de IA</p>
      </div>
      <div class="header-actions">
        <!-- Selector de tenant (solo admin) -->
        <Dropdown
          v-if="authStore.isAdmin"
          v-model="selectedTenantId"
          :options="allTenants"
          optionLabel="tenant_name"
          optionValue="tenant_id"
          placeholder="Todos los tenants"
          showClear
          class="tenant-selector"
          @change="loadUsage"
        />
        <Button icon="pi pi-refresh" text rounded @click="loadUsage" :loading="loading" />
      </div>
    </div>

    <div v-if="loading && !data" class="loading-state">
      <i class="pi pi-spin pi-spinner" style="font-size:2rem;color:#6366f1" />
      <p>Cargando datos de consumo...</p>
    </div>

    <template v-else-if="data">

      <!-- ── Tarjetas de resumen ─────────────────────────────────────────── -->
      <div class="summary-grid">

        <!-- Saldo de tokens -->
        <Card class="summary-card">
          <template #content>
            <div class="card-inner">
              <div class="card-icon tokens">
                <i class="pi pi-bolt" />
              </div>
              <div class="card-data">
                <div class="card-value">{{ fmt(data.balance.tokens_used) }}</div>
                <div class="card-label">Tokens usados</div>
                <div v-if="data.balance.tokens_included" class="card-sub">
                  de {{ fmt(data.balance.tokens_included) }} incluidos
                </div>
                <div v-else class="card-sub unlimited">Plan ilimitado</div>
              </div>
            </div>
            <!-- Barra de progreso -->
            <div v-if="data.balance.usage_pct !== null" class="usage-bar-wrap">
              <div class="usage-bar">
                <div
                  class="usage-fill"
                  :class="barClass(data.balance.usage_pct)"
                  :style="{ width: Math.min(data.balance.usage_pct, 100) + '%' }"
                />
              </div>
              <span :class="['usage-pct', barClass(data.balance.usage_pct)]">
                {{ data.balance.usage_pct }}%
              </span>
            </div>
          </template>
        </Card>

        <!-- Hoy (hora Colombia) -->
        <Card class="summary-card">
          <template #content>
            <div class="card-inner">
              <div class="card-icon day"><i class="pi pi-sun" /></div>
              <div class="card-data">
                <div class="card-value">{{ fmt(data.periods.today?.tokens ?? data.periods.last_24h.tokens) }}</div>
                <div class="card-label">Tokens hoy</div>
                <div class="card-sub">{{ (data.periods.today?.requests ?? data.periods.last_24h.requests) }} peticiones</div>
                <div class="card-sub" v-if="data.periods.today?.date">{{ fmtDateShort(data.periods.today.date) }}</div>
              </div>
            </div>
          </template>
        </Card>

        <!-- Últimos 7 días -->
        <Card class="summary-card">
          <template #content>
            <div class="card-inner">
              <div class="card-icon week"><i class="pi pi-calendar" /></div>
              <div class="card-data">
                <div class="card-value">{{ fmt(data.periods.last_7d.tokens) }}</div>
                <div class="card-label">Tokens (7 días)</div>
                <div class="card-sub">{{ data.periods.last_7d.requests }} peticiones</div>
              </div>
            </div>
          </template>
        </Card>

        <!-- Últimos 30 días vs período anterior -->
        <Card class="summary-card">
          <template #content>
            <div class="card-inner">
              <div class="card-icon month"><i class="pi pi-chart-line" /></div>
              <div class="card-data">
                <div class="card-value">{{ fmt(data.periods.last_30d.tokens) }}</div>
                <div class="card-label">Tokens (30 días)</div>
                <div v-if="data.periods.change_pct !== null" class="card-sub">
                  <span :class="data.periods.change_pct >= 0 ? 'up' : 'down'">
                    <i :class="data.periods.change_pct >= 0 ? 'pi pi-arrow-up' : 'pi pi-arrow-down'" />
                    {{ Math.abs(data.periods.change_pct) }}% vs mes anterior
                  </span>
                </div>
              </div>
            </div>
          </template>
        </Card>
      </div>

      <!-- ── Gráfico de consumo diario ──────────────────────────────────── -->
      <Card class="chart-card">
        <template #title>
          <div class="chart-header">
            <span>Consumo diario — últimos 30 días</span>
            <div class="chart-legend">
              <span class="legend-item tokens-legend">Tokens</span>
              <span class="legend-item requests-legend">Peticiones</span>
            </div>
          </div>
        </template>
        <template #content>
          <div class="chart-container">
            <div class="bar-chart">
              <div
                v-for="day in data.daily"
                :key="day.date"
                class="bar-col"
                v-tooltip="tooltipDay(day)"
              >
                <div class="bar-wrap">
                  <div
                    class="bar tokens-bar"
                    :style="{ height: barHeight(day.tokens, maxDailyTokens) + '%' }"
                  />
                </div>
                <div class="bar-label">{{ shortDate(day.date) }}</div>
              </div>
            </div>
          </div>
        </template>
      </Card>

      <!-- ── Consumo por proveedor ──────────────────────────────────────── -->
      <div class="bottom-grid">
        <Card>
          <template #title>Consumo por proveedor y modelo</template>
          <template #content>
            <DataTable :value="data.by_provider" size="small" responsiveLayout="scroll">
              <Column header="Proveedor / Modelo" style="min-width:180px">
                <template #body="{ data: row }">
                  <div class="provider-cell">
                    <Tag :value="row.provider" severity="info" size="small" />
                    <span class="model-name">{{ row.model || '—' }}</span>
                  </div>
                </template>
              </Column>
              <Column field="action_type" header="Tipo">
                <template #body="{ data: row }">
                  <Tag
                    :value="row.action_type"
                    :severity="actionSeverity(row.action_type)"
                    size="small"
                  />
                </template>
              </Column>
              <Column field="requests" header="Peticiones" sortable />
              <Column field="tokens" header="Tokens" sortable>
                <template #body="{ data: row }">
                  <span class="token-count">{{ fmt(row.tokens) }}</span>
                </template>
              </Column>
              <Column header="% del total">
                <template #body="{ data: row }">
                  <div class="pct-bar-wrap">
                    <div class="pct-bar">
                      <div
                        class="pct-fill"
                        :style="{ width: pctOfTotal(row.tokens) + '%' }"
                      />
                    </div>
                    <span class="pct-label">{{ pctOfTotal(row.tokens) }}%</span>
                  </div>
                </template>
              </Column>
              <template #empty>
                <p class="empty-msg">Sin datos de consumo por proveedor.</p>
              </template>
            </DataTable>
          </template>
        </Card>

        <!-- Últimas peticiones -->
        <Card>
          <template #title>Últimas peticiones</template>
          <template #content>
            <DataTable :value="data.recent_requests" size="small" responsiveLayout="scroll" :rows="10" paginator>
              <Column field="created_at" header="Fecha" style="min-width:140px">
                <template #body="{ data: row }">
                  {{ fmtDate(row.created_at) }}
                </template>
              </Column>
              <Column field="provider" header="Proveedor" />
              <Column field="action_type" header="Tipo">
                <template #body="{ data: row }">
                  <Tag :value="row.action_type" :severity="actionSeverity(row.action_type)" size="small" />
                </template>
              </Column>
              <Column field="tokens" header="Tokens">
                <template #body="{ data: row }">
                  <span class="token-count">{{ fmt(row.tokens) }}</span>
                </template>
              </Column>
              <Column field="status" header="Estado">
                <template #body="{ data: row }">
                  <Tag
                    :value="row.status"
                    :severity="row.status === 'success' ? 'success' : 'danger'"
                    size="small"
                  />
                </template>
              </Column>
              <template #empty>
                <p class="empty-msg">Sin peticiones registradas.</p>
              </template>
            </DataTable>
          </template>
        </Card>
      </div>

      <!-- ── Vista admin: todos los tenants ────────────────────────────── -->
      <Card v-if="authStore.isAdmin && allTenants.length > 0" class="mt-4">
        <template #title>Consumo por tenant</template>
        <template #content>
          <DataTable :value="allTenants" size="small" responsiveLayout="scroll" sortField="tokens_used" :sortOrder="-1">
            <Column field="tenant_name" header="Tenant" sortable />
            <Column field="plan" header="Plan" />
            <Column field="tokens_used" header="Tokens usados" sortable>
              <template #body="{ data: row }">
                <span class="token-count">{{ fmt(row.tokens_used) }}</span>
              </template>
            </Column>
            <Column header="Incluidos">
              <template #body="{ data: row }">
                {{ row.tokens_included ? fmt(row.tokens_included) : '∞' }}
              </template>
            </Column>
            <Column header="Uso %">
              <template #body="{ data: row }">
                <div v-if="row.tokens_included" class="pct-bar-wrap">
                  <div class="pct-bar">
                    <div
                      class="pct-fill"
                      :class="barClass(row.tokens_used / row.tokens_included * 100)"
                      :style="{ width: Math.min(row.tokens_used / row.tokens_included * 100, 100) + '%' }"
                    />
                  </div>
                  <span class="pct-label">{{ (row.tokens_used / row.tokens_included * 100).toFixed(1) }}%</span>
                </div>
                <span v-else class="text-muted">—</span>
              </template>
            </Column>
            <Column field="total_requests" header="Peticiones" sortable />
            <Column field="subscription_status" header="Estado">
              <template #body="{ data: row }">
                <Tag
                  :value="row.subscription_status"
                  :severity="{ active: 'success', past_due: 'warning', suspended: 'danger', inactive: 'secondary' }[row.subscription_status] || 'info'"
                  size="small"
                />
              </template>
            </Column>
            <Column header="">
              <template #body="{ data: row }">
                <Button
                  icon="pi pi-eye"
                  text
                  rounded
                  size="small"
                  @click="viewTenant(row.tenant_id)"
                />
              </template>
            </Column>
          </DataTable>
        </template>
      </Card>

    </template>
  </DefaultLayout>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useAuthStore } from '../stores/auth';
import DefaultLayout from '../components/layout/DefaultLayout.vue';
import Card from 'primevue/card';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import Button from 'primevue/button';
import Tag from 'primevue/tag';
import Dropdown from 'primevue/dropdown';
import api from '../services/api';

const authStore = useAuthStore();
const data = ref(null);
const allTenants = ref([]);
const loading = ref(false);
const selectedTenantId = ref(null);

// ── Helpers ───────────────────────────────────────────────────────────────────
const fmt = (n) => n != null ? Number(n).toLocaleString('es-CO') : '0';

const fmtDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleString('es-CO', {
    timeZone: 'America/Bogota',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
};

const fmtDateShort = (isoDate) => {
  if (!isoDate) return '—';
  const d = new Date(isoDate + 'T12:00:00Z');  // mediodía UTC para evitar desfase
  return d.toLocaleDateString('es-CO', {
    timeZone: 'America/Bogota',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

const shortDate = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr + 'T12:00:00Z');
  return d.toLocaleDateString('es-CO', {
    timeZone: 'America/Bogota',
    day: '2-digit',
    month: 'short',
  });
};

const tooltipDay = (day) => {
  const fecha = fmtDateShort(day.date);
  return `${fecha}\nTokens: ${fmt(day.tokens)}\nPeticiones: ${day.requests}`;
};

const barClass = (pct) => {
  if (pct >= 90) return 'critical';
  if (pct >= 70) return 'warning';
  return 'ok';
};

const actionSeverity = (type) => ({
  chat: 'info', stt: 'success', tts: 'warning',
}[type] || 'secondary');

const maxDailyTokens = computed(() => {
  if (!data.value?.daily?.length) return 1;
  return Math.max(...data.value.daily.map(d => d.tokens), 1);
});

const barHeight = (val, max) => max > 0 ? Math.max((val / max) * 100, val > 0 ? 4 : 0) : 0;

const totalTokens = computed(() =>
  data.value?.by_provider?.reduce((s, r) => s + (r.tokens || 0), 0) || 1
);

const pctOfTotal = (tokens) =>
  totalTokens.value > 0 ? Math.round((tokens || 0) / totalTokens.value * 100) : 0;

// ── Carga de datos ────────────────────────────────────────────────────────────
const loadUsage = async () => {
  loading.value = true;
  try {
    const params = selectedTenantId.value ? `?tenant_id=${selectedTenantId.value}` : '';
    const res = await api.get(`/usage/summary${params}`);
    data.value = res.data;
  } catch (e) {
    console.error(e);
  } finally {
    loading.value = false;
  }
};

const loadAllTenants = async () => {
  if (!authStore.isAdmin) return;
  try {
    const res = await api.get('/usage/tenants');
    allTenants.value = res.data;
  } catch (e) { console.error(e); }
};

const viewTenant = (tenantId) => {
  selectedTenantId.value = tenantId;
  loadUsage();
  window.scrollTo({ top: 0, behavior: 'smooth' });
};

onMounted(async () => {
  await Promise.all([loadUsage(), loadAllTenants()]);
});
</script>

<style scoped>
.page-header { display: flex; align-items: flex-start; justify-content: space-between; margin-bottom: 1.5rem; flex-wrap: wrap; gap: 1rem; }
.page-header h1 { margin: 0; color: #1e293b; }
.subtitle { color: #6b7280; margin: 0.25rem 0 0; font-size: 0.875rem; }
.header-actions { display: flex; align-items: center; gap: 0.5rem; }
.tenant-selector { min-width: 220px; }

.loading-state { text-align: center; padding: 4rem; color: #9ca3af; display: flex; flex-direction: column; align-items: center; gap: 0.75rem; }

/* Tarjetas de resumen */
.summary-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); gap: 1rem; margin-bottom: 1.5rem; }
.summary-card { }
.card-inner { display: flex; align-items: center; gap: 1rem; }
.card-icon { width: 48px; height: 48px; border-radius: 12px; display: flex; align-items: center; justify-content: center; font-size: 1.4rem; flex-shrink: 0; }
.card-icon.tokens { background: #eff6ff; color: #6366f1; }
.card-icon.day    { background: #f0fdf4; color: #16a34a; }
.card-icon.week   { background: #fef3c7; color: #b45309; }
.card-icon.month  { background: #fdf4ff; color: #9333ea; }
.card-value { font-size: 1.5rem; font-weight: 700; color: #1e293b; line-height: 1; }
.card-label { font-size: 0.8rem; color: #6b7280; margin-top: 0.2rem; }
.card-sub { font-size: 0.75rem; color: #9ca3af; margin-top: 0.15rem; }
.card-sub.unlimited { color: #10b981; font-weight: 600; }
.up { color: #ef4444; }
.down { color: #10b981; }

/* Barra de uso */
.usage-bar-wrap { display: flex; align-items: center; gap: 0.5rem; margin-top: 0.75rem; }
.usage-bar { flex: 1; height: 6px; background: #e5e7eb; border-radius: 3px; overflow: hidden; }
.usage-fill { height: 100%; border-radius: 3px; transition: width 0.5s; }
.usage-fill.ok { background: #10b981; }
.usage-fill.warning { background: #f59e0b; }
.usage-fill.critical { background: #ef4444; }
.usage-pct { font-size: 0.75rem; font-weight: 700; min-width: 40px; }
.usage-pct.ok { color: #10b981; }
.usage-pct.warning { color: #f59e0b; }
.usage-pct.critical { color: #ef4444; }

/* Gráfico de barras */
.chart-card { margin-bottom: 1.5rem; }
.chart-header { display: flex; align-items: center; justify-content: space-between; }
.chart-legend { display: flex; gap: 1rem; font-size: 0.78rem; }
.legend-item { display: flex; align-items: center; gap: 0.3rem; }
.legend-item::before { content: ''; width: 12px; height: 12px; border-radius: 2px; }
.tokens-legend::before { background: #6366f1; }
.requests-legend::before { background: #10b981; }

.chart-container { overflow-x: auto; }
.bar-chart { display: flex; align-items: flex-end; gap: 2px; height: 160px; padding-bottom: 24px; min-width: 600px; }
.bar-col { flex: 1; display: flex; flex-direction: column; align-items: center; gap: 2px; height: 100%; }
.bar-wrap { flex: 1; width: 100%; display: flex; align-items: flex-end; }
.bar { width: 100%; border-radius: 3px 3px 0 0; transition: height 0.3s; min-height: 0; }
.tokens-bar { background: #6366f1; opacity: 0.85; }
.bar-label { font-size: 0.6rem; color: #9ca3af; white-space: nowrap; transform: rotate(-45deg); transform-origin: center; }

/* Grid inferior */
.bottom-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 1.5rem; margin-bottom: 1.5rem; }
@media (max-width: 900px) { .bottom-grid { grid-template-columns: 1fr; } }

/* Tabla */
.provider-cell { display: flex; align-items: center; gap: 0.5rem; }
.model-name { font-size: 0.8rem; color: #6b7280; }
.token-count { font-weight: 600; color: #1e293b; font-family: monospace; }
.pct-bar-wrap { display: flex; align-items: center; gap: 0.4rem; }
.pct-bar { flex: 1; height: 6px; background: #e5e7eb; border-radius: 3px; overflow: hidden; min-width: 60px; }
.pct-fill { height: 100%; background: #6366f1; border-radius: 3px; }
.pct-fill.ok { background: #10b981; }
.pct-fill.warning { background: #f59e0b; }
.pct-fill.critical { background: #ef4444; }
.pct-label { font-size: 0.75rem; color: #6b7280; min-width: 35px; }
.text-muted { color: #9ca3af; }
.empty-msg { text-align: center; color: #9ca3af; padding: 1rem; }
.mt-4 { margin-top: 1.5rem; }
</style>
